import React from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import css from "@styled-system/css";
import { Link as ScrollLink } from "react-scroll";

import {
  Box,
  ColorMode,
  FadeWrapper,
  Flex,
  Heading,
  IconButton,
  Spacer,
  Text,
  VStack,
  Wrapper,
} from "components";

const CONTENT_MAX_WIDTH = 580;

function HomeHero({ image, headline, scrollPrompt }) {
  return (
    <ColorMode mode="dark">
      <Box bg="brand.primary">
        <Wrapper
          data-component-id="homeHero"
          position="relative"
          zIndex={2}
          overflow="visible"
        >
          <LargeGraphic image={image} offset={CONTENT_MAX_WIDTH} />
          <SmallGraphic image={image} />
          <FadeWrapper delay={0.2}>
            {headline && headline.text && (
              <Content
                title={headline.text}
                subtitle={scrollPrompt}
                maxWidth={CONTENT_MAX_WIDTH}
              />
            )}
          </FadeWrapper>
          <ScrollButton />
        </Wrapper>
      </Box>
    </ColorMode>
  );
}

function Content({ title, subtitle, maxWidth }) {
  return (
    <Flex
      data-component-id="homeHero.content"
      position="relative"
      flexDirection="column"
      justifyContent={{ _: "flex-start", lg: "flex-end" }}
      maxWidth={{ _: 256, md: 420, lg: maxWidth }}
      css={{
        // We use CSS variables for this to keep vertical height units static on iOS
        minHeight: "calc(var(--vh, 1vh) * 100)",
      }}
    >
      <Spacer spaceY={{ _: 32 + 32 + 16 + 20 + 64, lg: 48 + 32 + 64 }} />
      <VStack space="layout.3">
        <Box maxWidth="500px">
          <Heading children={title} as="h1" size={900} />
        </Box>
        <Text
          as="p"
          size={500}
          color="brand.secondary"
          style={{ cursor: "pointer" }}
        >
          <ScrollLink children={subtitle} to="home-body" smooth={true} />
        </Text>
      </VStack>
      <Spacer spaceY="layout.5" />
    </Flex>
  );
}

function LargeGraphic({ image, offset }) {
  return (
    <Box
      data-component-id="homeHero.largeGraphic"
      position="relative"
      display={{ _: "none", lg: "block" }}
      css={{ pointerEvents: "none" }}
    >
      <Box position="absolute" top={0} left={offset + 64}>
        <Box
          bg="brand.secondary"
          css={css({
            // We use CSS variables for this to keep vertical height units static on iOS
            minWidth: "calc(var(--vh, 1vh) * 166.65)",
            minHeight: "calc(var(--vh, 1vh) * 166.65)",
            clipPath: "polygon(30% 0, 100% 0%, 100% 100%, 100% 100%, 0 60%)",
          })}
        >
          <Box position="absolute" left={0} top={0} right={0} bottom={0}>
            <GatsbyImage
              image={getImage(image.gatsbyImageData)}
              alt={image.alt || ""}
              style={{ width: "100%", height: "100%" }}
              imgStyle={{ objectFit: "cover", objectPosition: "0% 100%" }}
            />
            {/* Soft top scrim */}
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              opacity={0.2}
              css={{
                background: "linear-gradient(to bottom, #000, transparent 33%)",
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SmallGraphic({ image }) {
  return (
    <Box
      data-component-id="homeHero.smallGraphic"
      position="relative"
      display={{ _: "block", lg: "none" }}
      css={{ pointerEvents: "none" }}
    >
      <Box
        position="absolute"
        top={0}
        right={{ _: -32, md: -48 }}
        left={{ _: -32, md: -48 }}
        overflowX="hidden"
      >
        <Box
          bg="brand.secondary"
          css={{
            // We use CSS variables for this to keep vertical height units static on iOS
            minWidth: "calc(var(--vh, 1vh) * 150)",
            minHeight: "calc(var(--vh, 1vh) * 150)",
            clipPath:
              "polygon(66.667% 0, 100% 0%, 100% 100%, 100% 100%, 0 66.667%)",
          }}
        >
          <Box position="absolute" left={0} top={0} right={0} bottom={0}>
            <GatsbyImage
              image={getImage(image.gatsbyImageData)}
              alt={image.alt || ""}
              style={{ width: "100%", height: "100%" }}
              imgStyle={{ objectFit: "cover", objectPosition: "0% 100%" }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function ScrollButton() {
  return (
    <Box
      data-component-id="homeHero.scrollButton"
      position="absolute"
      right={{ _: "initial", lg: "layout.5" }}
      bottom={{ _: "layout.3", md: "layout.4", lg: "layout.5" }}
      left={{ _: "layout.3", md: "layout.4", lg: "initial" }}
    >
      <ColorMode mode="light">
        <ScrollLink to="home-body" smooth={true}>
          <IconButton
            symbol="arrow-down-line"
            intent="default"
            appearance="primary"
            height={44}
            borderRadius={44}
          />
        </ScrollLink>
      </ColorMode>
    </Box>
  );
}

export default HomeHero;
